'use client'

import { useAtom } from 'jotai'
import { useTranslations } from 'next-intl'

import { DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu'
import { FilterRow } from '@/components/ui/filter-row'
import { HeatmapSizeFilterContent } from '@/features/chart/components/heatmap-size-filter'
import {
  DEFAULT_DECIMALS,
  PrecisionSettingContent,
} from '@/features/chart/components/precision-setting'
import {
  formatOrderbookHeatmapSize,
  type HeatmapSizeFilter,
  isHeatmapSizeFilterActive,
} from '@/features/chart/lib/orderbook-heatmap'
import { CHART_ENGINES, chartEngineAtom } from '@/state/storage'
import type { PerceptChartSettings } from '@/types/dashboard'

// Percept's Main Chart dialog options (its own settings button is hidden). Defaults mirror
// Percept's, so an unset key reads the way the chart draws it.
const PERCEPT_MAXIMA_MODES = ['off', 'basic', 'advanced'] as const

interface ChartSettingsProps {
  baseCurrency: string
  positionSizeUnit: 'base' | 'quote'
  setPositionSizeUnit: (value: 'base' | 'quote') => void
  showPositionLine: boolean
  setShowPositionLine: (v: boolean) => void
  showLiquidationLine: boolean
  setShowLiquidationLine: (v: boolean) => void
  showBuySellTriangles: boolean
  setShowBuySellTriangles: (v: boolean) => void
  showBuySellButtons: boolean
  setShowBuySellButtons: (v: boolean) => void
  showOpenOrders: boolean
  setShowOpenOrders: (v: boolean) => void
  showIndex: boolean
  setShowIndex: (v: boolean) => void
  showTopBar: boolean
  setShowTopBar: (v: boolean) => void
  showHighLow: boolean
  setShowHighLow: (v: boolean) => void
  perceptChartSettings: PerceptChartSettings
  setPerceptChartSettings: (patch: PerceptChartSettings) => void
  heatmapSizeFilter: HeatmapSizeFilter
  setHeatmapSizeFilter: (filter: HeatmapSizeFilter) => void
}

/** Bare content, rendered inside a WidgetMenuDropdownItem's DropdownMenuContent. */
export function ChartSettingsContent({
  baseCurrency,
  positionSizeUnit,
  setPositionSizeUnit,
  showPositionLine,
  setShowPositionLine,
  showLiquidationLine,
  setShowLiquidationLine,
  showBuySellTriangles,
  setShowBuySellTriangles,
  showBuySellButtons,
  setShowBuySellButtons,
  showOpenOrders,
  setShowOpenOrders,
  showIndex,
  setShowIndex,
  showTopBar,
  setShowTopBar,
  showHighLow,
  setShowHighLow,
  perceptChartSettings,
  setPerceptChartSettings,
  heatmapSizeFilter,
  setHeatmapSizeFilter,
}: ChartSettingsProps) {
  const t = useTranslations('trade.chartSettings')
  const [engine, setEngine] = useAtom(chartEngineAtom)
  // Both engines honor position line, open orders, buy/sell marks and buttons, and the top bar
  // (Percept's buy/sell buttons are PerceptBuySellButtons); the rest only
  // show for the engine that draws them (the value is kept either way).
  const isTradingView = engine === 'tradingview'

  const maximaMode = perceptChartSettings.maximaMode ?? 'off'
  const precisionMode = perceptChartSettings.precisionMode ?? 'default'
  const precisionLabel =
    precisionMode === 'manual'
      ? t('precisionModes.decimals', {
          count: perceptChartSettings.decimalPlaces ?? DEFAULT_DECIMALS,
        })
      : t(`precisionModes.${precisionMode}`)

  return (
    <div className="p-1">
      {/* Your orders and positions on the chart. */}
      <FilterRow label={t('groups.trading')} contentClassName="min-w-56">
        <FilterRow
          label={t('positionLine')}
          checked={showPositionLine}
          onCheckedChange={setShowPositionLine}
        />
        <FilterRow
          label={t('positionSizeUnit')}
          valueLabel={positionSizeUnit === 'base' ? baseCurrency : 'USD'}
        >
          {(['quote', 'base'] as const).map((unit) => (
            <FilterRow
              key={unit}
              label={unit === 'base' ? baseCurrency : 'USD'}
              checked={positionSizeUnit === unit}
              onCheckedChange={(checked) => checked && setPositionSizeUnit(unit)}
            />
          ))}
        </FilterRow>
        {/* TV only: Percept draws no liquidation line. */}
        {isTradingView && (
          <FilterRow
            label={t('liquidationLine')}
            checked={showLiquidationLine}
            onCheckedChange={setShowLiquidationLine}
          />
        )}
        <FilterRow
          label={t('openOrders')}
          checked={showOpenOrders}
          onCheckedChange={setShowOpenOrders}
        />
        <FilterRow
          label={t('buySellTriangles')}
          checked={showBuySellTriangles}
          onCheckedChange={setShowBuySellTriangles}
        />
        <FilterRow
          label={t('buySellButtons')}
          checked={showBuySellButtons}
          onCheckedChange={setShowBuySellButtons}
        />
      </FilterRow>

      {/* What this chart shows: on/off toggles first, then the settings that open further.
          Percept's rows are its Main Chart options; TradingView has its own settings dialog. */}
      <FilterRow label={t('groups.display')} contentClassName="min-w-56">
        <FilterRow label={t('topBar')} checked={showTopBar} onCheckedChange={setShowTopBar} />
        {isTradingView ? (
          <FilterRow label={t('showIndex')} checked={showIndex} onCheckedChange={setShowIndex} />
        ) : (
          <>
            <FilterRow
              label={t('highLow')}
              checked={showHighLow}
              onCheckedChange={setShowHighLow}
            />
            <FilterRow
              label={t('priceLine')}
              checked={perceptChartSettings.showPriceLine ?? true}
              onCheckedChange={(showPriceLine) => setPerceptChartSettings({ showPriceLine })}
            />
            <FilterRow
              label={t('candleCountdown')}
              checked={perceptChartSettings.candleCountdown ?? true}
              onCheckedChange={(candleCountdown) => setPerceptChartSettings({ candleCountdown })}
            />
            <FilterRow
              label={t('momentumArrows')}
              checked={perceptChartSettings.momentumArrows ?? true}
              onCheckedChange={(momentumArrows) => setPerceptChartSettings({ momentumArrows })}
            />
            <FilterRow
              label={t('offscreenLivePill')}
              checked={perceptChartSettings.showOffscreenLivePill ?? false}
              onCheckedChange={(showOffscreenLivePill) =>
                setPerceptChartSettings({ showOffscreenLivePill })
              }
            />
          </>
        )}
        <DropdownMenuSeparator />
        {!isTradingView && (
          <>
            <FilterRow
              label={t('maxima')}
              valueLabel={t(`maximaModes.${maximaMode}`)}
              contentClassName="min-w-40"
            >
              {PERCEPT_MAXIMA_MODES.map((value) => (
                <FilterRow
                  key={value}
                  label={t(`maximaModes.${value}`)}
                  checked={maximaMode === value}
                  onCheckedChange={(checked) =>
                    checked && setPerceptChartSettings({ maximaMode: value })
                  }
                />
              ))}
            </FilterRow>
            <FilterRow label={t('precision')} valueLabel={precisionLabel}>
              <PrecisionSettingContent
                settings={perceptChartSettings}
                onChange={setPerceptChartSettings}
              />
            </FilterRow>
          </>
        )}
        {/* Both engines: the order-book heatmap's size filter (also in the indicator's inputs). */}
        <FilterRow
          label={t('heatmapFilter')}
          valueLabel={
            isHeatmapSizeFilterActive(heatmapSizeFilter)
              ? `${formatOrderbookHeatmapSize(heatmapSizeFilter.min) || '0'} – ${
                  formatOrderbookHeatmapSize(heatmapSizeFilter.max) || '∞'
                }`
              : t('heatmapFilterOff')
          }
        >
          <HeatmapSizeFilterContent filter={heatmapSizeFilter} onChange={setHeatmapSizeFilter} />
        </FilterRow>
      </FilterRow>

      {/* App-wide, like General settings: these change every chart, not just this one. */}
      <DropdownMenuSeparator />
      <DropdownMenuLabel>{t('groups.allCharts')}</DropdownMenuLabel>
      <FilterRow
        label={t('groups.engine')}
        valueLabel={t(`engines.${engine}`)}
        contentClassName="min-w-48"
      >
        {CHART_ENGINES.map((value) => (
          <FilterRow
            key={value}
            label={t(`engines.${value}`)}
            checked={engine === value}
            onCheckedChange={(checked) => checked && setEngine(value)}
          />
        ))}
      </FilterRow>
    </div>
  )
}
